import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  ButtonBase,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { borderRadius, getModernScrollbar, layoutGap } from '../../../theme/theme';
import TourArrowLayer from './TourArrowLayer';
import { TourStepProgressDots } from './TourStepProgressDots';
import { clamp, computeCalloutLayout } from './tourCalloutLayout';
import {
  getDetailToneStyles,
  TourScrollHint,
  useMeasuredElementHeight,
  useTourScrollState,
} from './workspaceSpotlightTourSupport';
import type {
  RelatedSpotlightRect,
  SpotlightRect,
  WorkspaceSpotlightTourProps,
  WorkspaceSpotlightTourRelatedTarget,
  WorkspaceSpotlightTourStep,
} from './WorkspaceSpotlightTour.types';

export { computeCalloutLayout };
export type {
  SpotlightRect,
  WorkspaceSpotlightTourDetail,
  WorkspaceSpotlightTourDetailTone,
  WorkspaceSpotlightTourRelatedTarget,
  WorkspaceSpotlightTourStep,
} from './WorkspaceSpotlightTour.types';

const PADDING = 8;
const MIN_SPOTLIGHT_SIZE = 32;
const MIN_SPOTLIGHT_THICKNESS = 20;
const MIN_MEASURABLE_TARGET_THICKNESS = 8;
const SPOTLIGHT_RECT_TOLERANCE = 1;
const TARGET_READY_TIMEOUT_MS = 600;
// Settle-driven measurement: re-measure per animation frame while the target
// is still moving (mount, open animation, scroll), then go idle instead of
// polling for the whole tour. Any layout signal (scroll/resize/observer)
// re-arms the loop.
const SETTLE_STABLE_FRAMES = 6;
const SETTLE_LOOP_MAX_MS = 2000;

const normalizeBullets = (bullets: readonly string[] | undefined) =>
  Array.from(
    new Set(
      (bullets ?? [])
        .map((bullet) => bullet.trim())
        .filter(Boolean)
    )
  );

const normalizeStepDetails = (step: WorkspaceSpotlightTourStep) =>
  step.details
    .map((detail) => ({
      label: detail.label.trim(),
      bullets: normalizeBullets(detail.bullets),
      tone: detail.tone ?? 'default',
    }))
    .filter((detail, index, allDetails) => {
      if (!detail.label || detail.bullets.length === 0) {
        return false;
      }

      return (
        allDetails.findIndex(
          (candidate) => candidate.label.toLocaleLowerCase() === detail.label.toLocaleLowerCase()
        ) === index
      );
    })
    .slice(0, 3);

const createFallbackRect = (
  viewportWidth: number,
  viewportHeight: number,
  fallbackRect?: SpotlightRect
): SpotlightRect => {
  if (fallbackRect) {
    return fallbackRect;
  }

  return {
    top: viewportHeight * 0.16,
    left: viewportWidth * 0.08,
    width: viewportWidth * 0.58,
    height: Math.min(220, viewportHeight * 0.28),
  };
};

const isUsableRect = (rect: DOMRect | undefined | null) =>
  Boolean(
    rect &&
      rect.width >= MIN_SPOTLIGHT_THICKNESS &&
      Math.min(rect.width, rect.height) >= MIN_MEASURABLE_TARGET_THICKNESS
  );

const areSpotlightRectsEqual = (
  previousRect: SpotlightRect | null,
  nextRect: SpotlightRect
) =>
  Boolean(
    previousRect &&
      Math.abs(previousRect.top - nextRect.top) <= SPOTLIGHT_RECT_TOLERANCE &&
      Math.abs(previousRect.left - nextRect.left) <= SPOTLIGHT_RECT_TOLERANCE &&
      Math.abs(previousRect.width - nextRect.width) <= SPOTLIGHT_RECT_TOLERANCE &&
      Math.abs(previousRect.height - nextRect.height) <= SPOTLIGHT_RECT_TOLERANCE
  );

const areRelatedSpotlightRectsEqual = (
  previousRects: readonly RelatedSpotlightRect[],
  nextRects: readonly RelatedSpotlightRect[]
) => {
  if (previousRects.length !== nextRects.length) {
    return false;
  }

  return previousRects.every((previousRect, index) => {
    const nextRect = nextRects[index];
    return (
      previousRect.label === nextRect.label &&
      previousRect.visualLabel === nextRect.visualLabel &&
      previousRect.affectsCalloutLayout === nextRect.affectsCalloutLayout &&
      areSpotlightRectsEqual(previousRect.rect, nextRect.rect)
    );
  });
};

const getFocusableElements = (container: HTMLElement | null): HTMLElement[] => {
  if (!container) {
    return [];
  }

  const focusableCandidates = container.querySelectorAll<HTMLElement>(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  return Array.from(focusableCandidates).filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      element.tabIndex !== -1 &&
      element.getAttribute('aria-hidden') !== 'true'
  );
};

const createSpotlightRectFromDomRect = (
  measuredRect: DOMRect,
  viewportWidth: number,
  viewportHeight: number,
  padding = PADDING
): SpotlightRect => ({
  top: Math.max(0, measuredRect.top - padding),
  left: Math.max(0, measuredRect.left - padding),
  width: Math.max(
    MIN_SPOTLIGHT_SIZE,
    Math.min(viewportWidth, measuredRect.right + padding) -
      Math.max(0, measuredRect.left - padding)
  ),
  height: Math.max(
    MIN_SPOTLIGHT_SIZE,
    Math.min(viewportHeight, measuredRect.bottom + padding) -
      Math.max(0, measuredRect.top - padding)
  ),
});

const measureSpotlightTarget = (
  target: HTMLElement | null | undefined,
  padding = PADDING
): SpotlightRect | null => {
  const viewportWidth = window.innerWidth || 1280;
  const viewportHeight = window.innerHeight || 800;
  const rect = target?.getBoundingClientRect();

  if (!isUsableRect(rect)) {
    return null;
  }

  return createSpotlightRectFromDomRect(rect as DOMRect, viewportWidth, viewportHeight, padding);
};

const measureFallbackSpotlightRect = (fallbackRect?: SpotlightRect): SpotlightRect => {
  const viewportWidth = window.innerWidth || 1280;
  const viewportHeight = window.innerHeight || 800;

  return createFallbackRect(viewportWidth, viewportHeight, fallbackRect);
};

const measureRelatedSpotlightRects = (
  relatedTargets: readonly WorkspaceSpotlightTourRelatedTarget[] | undefined,
  observeTarget: (target: HTMLElement | null) => void
): RelatedSpotlightRect[] =>
  (relatedTargets ?? [])
    .map((relatedTarget): RelatedSpotlightRect | null => {
      const label = relatedTarget.label.trim();
      const visualLabel = relatedTarget.visualLabel?.trim() || label;
      const relatedElement = relatedTarget.targetRef?.current ?? relatedTarget.resolveTarget?.() ?? null;
      const relatedRect = measureSpotlightTarget(relatedElement);

      if (relatedElement && relatedRect) {
        observeTarget(relatedElement);
      }

      const rect = relatedRect ?? (
        relatedTarget.fallbackRect ? measureFallbackSpotlightRect(relatedTarget.fallbackRect) : null
      );
      return label && rect
        ? {
            label,
            visualLabel,
            rect,
            affectsCalloutLayout: relatedTarget.affectsCalloutLayout !== false,
          }
        : null;
    })
    .filter((rect): rect is RelatedSpotlightRect => Boolean(rect));

const EMPTY_CHROME_TARGETS: Record<string, SpotlightRect> = {};

const WorkspaceSpotlightTour: React.FC<WorkspaceSpotlightTourProps> = ({
  open,
  steps,
  stepIndex,
  onBack,
  onNext,
  onJumpToStep,
  onClose,
  dialogLabel,
  finalActionLabel = 'Start working',
  nextActionLabel = 'Next focus',
  showArrows = false,
  chromeTargets = EMPTY_CHROME_TARGETS,
}) => {
  const theme = useTheme();
  const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
  const [relatedSpotlightRects, setRelatedSpotlightRects] = useState<RelatedSpotlightRect[]>([]);
  const [showStepList, setShowStepList] = useState(false);
  const spotlightRectRef = useRef<SpotlightRect | null>(null);
  const relatedSpotlightRectsRef = useRef<RelatedSpotlightRect[]>([]);
  const [calloutElement, setCalloutElement] = useState<HTMLDivElement | null>(null);

  const currentStep = steps[stepIndex] ?? null, spotlightReady = spotlightRect !== null;
  const { scrollBodyRef, hasMoreContent } = useTourScrollState(currentStep?.id ?? '');
  const measuredCalloutHeight = useMeasuredElementHeight(calloutElement, currentStep?.id ?? '');

  useLayoutEffect(() => {
    if (!open || !currentStep) {
      if (spotlightRectRef.current !== null || relatedSpotlightRectsRef.current.length > 0) {
        setSpotlightRect(null);
        setRelatedSpotlightRects([]);
        spotlightRectRef.current = null;
        relatedSpotlightRectsRef.current = [];
      }
      return;
    }

    let frameId = 0;
    let resizeObserver: ResizeObserver | null = null;
    const observedTargets = new Set<HTMLElement>();
    let scrolledTarget: HTMLElement | null = null;
    const targetWaitStartedAt = window.performance?.now?.() ?? Date.now();

    spotlightRectRef.current = null;
    setSpotlightRect(null);
    if (relatedSpotlightRectsRef.current.length > 0) {
      relatedSpotlightRectsRef.current = [];
      setRelatedSpotlightRects([]);
    }

    const observeTarget = (target: HTMLElement | null) => {
      if (typeof ResizeObserver === 'undefined' || !target || observedTargets.has(target)) {
        return;
      }

      if (!resizeObserver) {
        resizeObserver = new ResizeObserver(wakeSettleLoop);
      }

      resizeObserver.observe(target);
      observedTargets.add(target);
    };

    const scrollTargetIntoView = (target: HTMLElement) => {
      if (scrolledTarget === target) {
        return;
      }

      target.scrollIntoView?.({
        block: currentStep.scrollTargetBlock ?? 'nearest',
        inline: 'nearest',
      });
      scrolledTarget = target;
    };

    const measureRelatedTargets = (): RelatedSpotlightRect[] =>
      measureRelatedSpotlightRects(currentStep.relatedTargets, observeTarget);

    const refreshRelatedRects = (): boolean => {
      const nextRelatedRects = measureRelatedTargets();
      if (areRelatedSpotlightRectsEqual(relatedSpotlightRectsRef.current, nextRelatedRects)) {
        return false;
      }

      relatedSpotlightRectsRef.current = nextRelatedRects;
      setRelatedSpotlightRects(nextRelatedRects);
      return true;
    };

    const refreshRect = (): 'waiting' | 'changed' | 'stable' => {
      const chromeTargetRect = currentStep.chromeTargetId
        ? chromeTargets[currentStep.chromeTargetId]
        : undefined;
      const targetElement = !chromeTargetRect ? (currentStep.targetRef?.current ?? null) : null;
      const targetRect = chromeTargetRect ?? measureSpotlightTarget(targetElement, currentStep.spotlightPadding);

      if (targetElement && targetRect && !chromeTargetRect) {
        observeTarget(targetElement);
        scrollTargetIntoView(targetElement);
      }

      const elapsed = (window.performance?.now?.() ?? Date.now()) - targetWaitStartedAt;
      if (!chromeTargetRect && currentStep.targetRef && !targetElement && elapsed < TARGET_READY_TIMEOUT_MS) {
        return 'waiting';
      }

      const nextRect = targetRect ?? measureFallbackSpotlightRect(currentStep.fallbackRect);
      const primaryChanged = !areSpotlightRectsEqual(spotlightRectRef.current, nextRect);
      if (primaryChanged) {
        spotlightRectRef.current = nextRect;
        setSpotlightRect(nextRect);
      }

      const relatedChanged = refreshRelatedRects();
      return primaryChanged || relatedChanged ? 'changed' : 'stable';
    };

    let settleLoopRunning = false;
    let stableFrameCount = 0;
    let settleLoopStartedAt = targetWaitStartedAt;

    const runSettleFrame = () => {
      frameId = 0;
      const status = refreshRect();

      stableFrameCount = status === 'stable' ? stableFrameCount + 1 : 0;
      const now = window.performance?.now?.() ?? Date.now();
      const settled = stableFrameCount >= SETTLE_STABLE_FRAMES;
      // 'waiting' never expires the loop on its own — TARGET_READY_TIMEOUT_MS
      // inside refreshRect bounds how long a missing target blocks the fallback.
      const expired = status !== 'waiting' && now - settleLoopStartedAt >= SETTLE_LOOP_MAX_MS;

      if (settled || expired) {
        settleLoopRunning = false;
        return;
      }

      frameId = window.requestAnimationFrame(runSettleFrame);
    };

    const wakeSettleLoop = () => {
      stableFrameCount = 0;
      settleLoopStartedAt = window.performance?.now?.() ?? Date.now();

      if (!settleLoopRunning) {
        settleLoopRunning = true;
        frameId = window.requestAnimationFrame(runSettleFrame);
      }
    };

    wakeSettleLoop();
    window.addEventListener('resize', wakeSettleLoop);
    window.addEventListener('scroll', wakeSettleLoop, true);

    return () => {
      if (frameId !== 0) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      window.removeEventListener('resize', wakeSettleLoop);
      window.removeEventListener('scroll', wakeSettleLoop, true);
    };
  }, [currentStep, open, chromeTargets]);

  useEffect(() => {
    if (!open) {
      spotlightRectRef.current = null;
      relatedSpotlightRectsRef.current = [];
      setRelatedSpotlightRects([]);
      setShowStepList(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onNext();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onBack();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = getFocusableElements(calloutElement);
      if (focusableElements.length === 0) {
        return;
      }

      const activeElement = document.activeElement as HTMLElement | null;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const focusInsideCallout = Boolean(activeElement && calloutElement?.contains(activeElement));

      if (!focusInsideCallout) {
        event.preventDefault();
        firstElement.focus();
        return;
      }

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [calloutElement, onBack, onClose, onNext, open]);

  useEffect(() => {
    if (!open || !spotlightReady) {
      return;
    }

    const focusFirstAvailableElement = () => {
      const focusableElements = getFocusableElements(calloutElement);
      (focusableElements[0] ?? calloutElement)?.focus();
    };

    focusFirstAvailableElement();
    const focusTimeoutId = window.setTimeout(focusFirstAvailableElement, 0);
    const focusFrameId = window.requestAnimationFrame(focusFirstAvailableElement);

    return () => {
      window.clearTimeout(focusTimeoutId);
      window.cancelAnimationFrame(focusFrameId);
    };
  }, [calloutElement, currentStep?.id, open, spotlightReady]);

  if (!open || !currentStep || !spotlightRect) {
    return null;
  }

  const isLastStep = stepIndex === steps.length - 1;
  const viewportWidth = window.innerWidth || 1280;
  const viewportHeight = window.innerHeight || 800;
  const compactOnNarrowViewport = Boolean(
    currentStep.compactOnNarrowViewport && viewportWidth < 600
  );
  const calloutLayout = computeCalloutLayout(
    spotlightRect,
    viewportWidth,
    viewportHeight,
    relatedSpotlightRects
      .filter(({ affectsCalloutLayout }) => affectsCalloutLayout)
      .map(({ rect }) => rect),
    currentStep.keepSpotlightClear
  );
  const renderedDetails = normalizeStepDetails(currentStep);
  const focusLabel = currentStep.focusLabel?.trim();
  const relatedTargetsLabel = currentStep.relatedTargetsLabel?.trim() || 'Related';
  const handleJumpAndCollapse = (index: number) => {
    onJumpToStep(clamp(index, 0, Math.max(0, steps.length - 1)));
    setShowStepList(false);
  };
  const currentProgressLabel = currentStep.phaseLabel
    ? `${currentStep.phaseLabel} - ${stepIndex + 1}/${steps.length}`
    : `${stepIndex + 1}/${steps.length}`;
  const overlayMaskId = `workspace-spotlight-mask-${currentStep.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const isChromeTargetSpotlight = Boolean(
    currentStep.chromeTargetId && chromeTargets[currentStep.chromeTargetId]
  );
  const overlayCutouts = [
    ...(!isChromeTargetSpotlight ? [{ rect: spotlightRect, primary: true }] : []),
    ...relatedSpotlightRects.map(({ rect }) => ({ rect, primary: false })),
  ];
  const primaryArrowRect = isChromeTargetSpotlight
    ? {
        ...spotlightRect,
        top: 0,
        height: 1,
      }
    : spotlightRect;

  const tourOverlay = (
    <Box
      role="dialog"
      aria-label={dialogLabel}
      aria-modal="true"
      data-testid="workspace-spotlight-tour"
      data-tour-step-id={currentStep.id}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.tooltip + 1,
        pointerEvents: 'none',
      }}
    >
      <svg
        aria-hidden="true"
        focusable="false"
        width={viewportWidth}
        height={viewportHeight}
        viewBox={`0 0 ${viewportWidth} ${viewportHeight}`}
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'auto',
        }}
      >
        <defs>
          <mask id={overlayMaskId}>
            <rect x="0" y="0" width={viewportWidth} height={viewportHeight} fill="white" />
            {overlayCutouts.map(({ rect, primary }, index) => (
              <rect
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                rx={primary ? borderRadius.lg : borderRadius.md}
                fill="black"
              />
            ))}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width={viewportWidth}
          height={viewportHeight}
          fill={alpha(theme.palette.background.default, 0.66)}
          mask={`url(#${overlayMaskId})`}
        />
      </svg>

      {!isChromeTargetSpotlight && (
        <Box
          data-testid="workspace-spotlight-highlight"
          data-tour-step-id={currentStep.id}
          sx={{
            position: 'fixed',
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
            borderRadius: `${borderRadius.lg}px`,
            boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.light, 0.78)}, 0 6px 18px ${alpha(theme.palette.primary.main, 0.14)}`,
            outline: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
            outlineOffset: '3px',
            pointerEvents: currentStep.allowTargetInteraction ? 'none' : 'auto',
          }}
        />
      )}

      {relatedSpotlightRects.map(({ label, rect }) => (
        <Box
          key={`${currentStep.id}-${label}`}
          aria-hidden="true"
          data-testid="workspace-spotlight-related-highlight"
          data-tour-step-id={currentStep.id}
          data-related-target-label={label}
          sx={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius: `${borderRadius.md}px`,
            border: `2px solid ${alpha(theme.palette.secondary.light, 0.92)}`,
            boxShadow: [
              `0 0 0 3px ${alpha(theme.palette.background.default, 0.78)}`,
              `0 0 0 6px ${alpha(theme.palette.secondary.main, 0.24)}`,
              `0 8px 18px ${alpha(theme.palette.secondary.main, 0.16)}`,
            ].join(', '),
            outline: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
            outlineOffset: '2px',
            pointerEvents: 'none',
          }}
        />
      ))}
      <Paper
        ref={setCalloutElement}
        elevation={0}
        data-testid="workspace-spotlight-callout"
        data-tour-step-id={currentStep.id}
        tabIndex={-1}
        sx={{
          position: 'fixed',
          top: calloutLayout.top,
          left: calloutLayout.left,
          width: calloutLayout.width,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: calloutLayout.maxHeight,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: `${borderRadius.lg}px`,
          border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.96),
          boxShadow: `0 14px 24px ${alpha(theme.palette.common.black, 0.2)}`,
          backdropFilter: 'blur(8px)',
          overflow: 'hidden',
          pointerEvents: 'auto',
        }}
      >
        <Box
          sx={{
            px: 1,
            py: compactOnNarrowViewport ? layoutGap.micro : 0.85,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            backgroundColor: alpha(theme.palette.background.default, 0.08),
          }}
        >
          <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" justifyContent="space-between">
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: 0,
                textTransform: 'uppercase',
                color: alpha(theme.palette.primary.light, 0.9),
              }}
            >
              {currentProgressLabel}
            </Typography>
            <Stack direction="row" spacing={layoutGap.snug} alignItems="center">
              <Button
                size="small"
                variant="text"
                onClick={() => setShowStepList((current) => !current)}
                aria-expanded={showStepList}
                sx={{
                  minWidth: 0,
                  px: layoutGap.dense,
                  fontSize: '0.72rem',
                  color: alpha(theme.palette.text.secondary, 0.86),
                }}
              >
                {showStepList ? 'Hide step list' : 'Step list'}
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={onClose}
                sx={{
                  minWidth: 0,
                  px: layoutGap.dense,
                  fontSize: '0.72rem',
                  color: alpha(theme.palette.text.secondary, 0.86),
                }}
              >
                Skip tour
              </Button>
            </Stack>
          </Stack>
          <Typography
            variant="subtitle1"
            sx={{ mt: compactOnNarrowViewport ? 0 : 0.55, fontWeight: 700, lineHeight: 1.18, fontSize: '0.96rem', textWrap: 'balance' }}
          >
            {currentStep.title}
          </Typography>
          {showStepList && (
            <Box
              data-testid="workspace-spotlight-step-list"
              sx={{
                mt: 0.8,
                px: 0.35,
                py: 0.35,
                maxHeight: 148,
                overflowY: 'auto',
                borderRadius: `${borderRadius.md}px`,
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.54),
              }}
            >
              <Stack spacing={layoutGap.note}>
                {steps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    {step.phaseLabel && (
                      index === 0 || step.phaseLabel !== steps[index - 1]?.phaseLabel
                    ) && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          px: 0.65,
                          pt: index === 0 ? 0.15 : 0.65,
                          pb: 0.15,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          letterSpacing: 0,
                          textTransform: 'uppercase',
                          color: alpha(theme.palette.primary.light, 0.82),
                        }}
                      >
                        {step.phaseLabel}
                      </Typography>
                    )}
                    <ButtonBase
                      onClick={() => handleJumpAndCollapse(index)}
                      aria-current={index === stepIndex ? 'step' : undefined}
                      aria-label={`Jump to tour step ${index + 1}`}
                      sx={{
                        width: '100%',
                        px: 0.65,
                        py: layoutGap.dense,
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        borderRadius: `${borderRadius.md}px`,
                        border: `1px solid ${
                          index === stepIndex
                            ? alpha(theme.palette.primary.main, 0.26)
                            : 'transparent'
                        }`,
                        backgroundColor:
                          index === stepIndex
                            ? alpha(theme.palette.primary.main, 0.08)
                            : 'transparent',
                        '&:hover': {
                          backgroundColor:
                            index === stepIndex
                              ? alpha(theme.palette.primary.main, 0.12)
                              : alpha(theme.palette.background.default, 0.12),
                        },
                      }}
                    >
                      <Stack direction="row" spacing={layoutGap.loft} alignItems="flex-start" sx={{ minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            minWidth: 22,
                            fontWeight: 800,
                            color:
                              index === stepIndex
                                ? theme.palette.primary.light
                                : alpha(theme.palette.text.secondary, 0.86),
                          }}
                        >
                          {index + 1}.
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            minWidth: 0,
                            fontWeight: index === stepIndex ? 700 : 600,
                            color: alpha(theme.palette.text.primary, 0.94),
                            lineHeight: 1.35,
                          }}
                        >
                          {step.title}
                        </Typography>
                      </Stack>
                    </ButtonBase>
                  </React.Fragment>
                ))}
              </Stack>
            </Box>
          )}
          {!compactOnNarrowViewport && (
            <TourStepProgressDots
              steps={steps}
              stepIndex={stepIndex}
              onJumpToStep={handleJumpAndCollapse}
            />
          )}
        </Box>

        <Stack
          ref={scrollBodyRef}
          data-testid="workspace-spotlight-scroll-body"
          spacing={compactOnNarrowViewport ? layoutGap.fine : layoutGap.comfort}
          sx={{
            flex: 1,
            minHeight: 0,
            px: 1,
            py: compactOnNarrowViewport ? 0 : 0.9,
            overflowY: 'scroll',
            '& > *': { flexShrink: 0 },
            ...getModernScrollbar(theme),
          }}
        >
          {focusLabel && (
            <Box
              data-testid="workspace-spotlight-focus-note"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: layoutGap.row,
                px: 0.65,
                py: compactOnNarrowViewport ? 0 : 0.45,
                borderRadius: `${borderRadius.md}px`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.055),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.66rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 0,
                  color: alpha(theme.palette.primary.light, 0.88),
                  flexShrink: 0,
                }}
              >
                Focus
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  minWidth: 0,
                  color: alpha(theme.palette.text.primary, 0.9),
                  fontSize: compactOnNarrowViewport ? '0.6875rem' : '0.78rem',
                  lineHeight: compactOnNarrowViewport ? 1.1 : 1.35,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {focusLabel}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography
              variant="body2"
              sx={{ lineHeight: compactOnNarrowViewport ? 1.15 : 1.5 }}
            >
              {currentStep.intro}
            </Typography>
          </Box>
          {relatedSpotlightRects.length > 0 && (
            <Box
              role="note"
              aria-label={`Related highlighted controls. ${relatedTargetsLabel} ${relatedSpotlightRects
                .map(({ label }) => label)
                .join(' / ')}`}
              data-testid="workspace-spotlight-related-note"
              sx={{
                display: 'flex',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                alignItems: 'center',
                gap: layoutGap.row,
                px: 0.65,
                py: compactOnNarrowViewport ? 0 : 0.45,
                borderRadius: `${borderRadius.md}px`,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.66rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 0,
                  color: alpha(theme.palette.primary.light, 0.88),
                  flexShrink: 0,
                }}
              >
                {relatedTargetsLabel}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  minWidth: 0,
                  color: alpha(theme.palette.text.primary, 0.9),
                  fontSize: compactOnNarrowViewport ? '0.6875rem' : '0.78rem',
                  lineHeight: compactOnNarrowViewport ? 1.1 : 1.35,
                  overflowWrap: 'anywhere',
                }}
              >
                {relatedSpotlightRects.map(({ visualLabel }) => visualLabel).join(' / ')}
              </Typography>
            </Box>
          )}
          {renderedDetails.map((detail) => {
            const toneStyles = getDetailToneStyles(detail.tone, theme);

            return (
              <Box
                key={detail.label}
                sx={{
                  px: 0.7,
                  py: compactOnNarrowViewport ? layoutGap.fine : 0.6,
                  borderRadius: `${borderRadius.md}px`,
                  border: `1px solid ${toneStyles.borderColor}`,
                  backgroundColor: toneStyles.backgroundColor,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: compactOnNarrowViewport ? layoutGap.nano : 0.35,
                    fontSize: '0.66rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0,
                    color: toneStyles.labelColor,
                  }}
                >
                  {detail.label}
                </Typography>
                <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
                  {detail.bullets.map((bullet) => (
                    <Typography
                      key={bullet}
                      component="li"
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.text.primary, 0.92),
                        lineHeight: compactOnNarrowViewport ? 1.3 : 1.42,
                        '& + li': {
                          mt: compactOnNarrowViewport ? layoutGap.nano : 0.28,
                        },
                      }}
                    >
                      {bullet}
                    </Typography>
                  ))}
                </Box>
              </Box>
            );
          })}
          <Box
            data-testid="workspace-spotlight-next-section"
            sx={{
              mx: 1,
              mb: compactOnNarrowViewport ? layoutGap.fine : 0.9,
              px: 0.7,
              py: compactOnNarrowViewport ? layoutGap.fine : 0.6,
              borderRadius: `${borderRadius.md}px`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block', mb: compactOnNarrowViewport ? layoutGap.nano : layoutGap.micro,
                fontSize: '0.66rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0,
                color: alpha(theme.palette.primary.light, 0.88),
              }}
            >
              Next
            </Typography>
            <Typography
              variant="body2"
              sx={{ lineHeight: compactOnNarrowViewport ? 1.3 : 1.42 }}
            >
              {currentStep.nextAction}
            </Typography>
          </Box>
        </Stack>
        <TourScrollHint visible={hasMoreContent} />
        <Box data-testid="workspace-spotlight-actions"
          sx={{
            px: 1,
            py: compactOnNarrowViewport ? 0 : 0.75,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Button
            size="small"
            variant="text"
            onClick={onBack}
            disabled={stepIndex === 0}
            sx={{
              color: alpha(theme.palette.text.secondary, stepIndex === 0 ? 0.4 : 0.9),
            }}
          >
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={isLastStep ? onClose : onNext}
            sx={{
              borderRadius: `${borderRadius.md}px`,
              px: 1.2,
              boxShadow: 'none',
            }}
          >
            {isLastStep ? finalActionLabel : nextActionLabel}
          </Button>
        </Box>
      </Paper>
      {(() => {
        const shouldShowArrows = showArrows
          ? currentStep.showArrow !== false
          : currentStep.showArrow === true;

        return (
          shouldShowArrows && (
            <TourArrowLayer
              calloutRect={{
                top: calloutLayout.top,
                left: calloutLayout.left,
                width: calloutLayout.width,
                height: measuredCalloutHeight ?? calloutLayout.maxHeight,
              }}
              primaryRect={primaryArrowRect}
              relatedRects={relatedSpotlightRects.map(({ label, rect }) => ({ label, rect }))}
            />
          )
        );
      })()}
    </Box>
  );

  if (typeof document === 'undefined') {
    return tourOverlay;
  }

  return createPortal(tourOverlay, document.body);
};

export default WorkspaceSpotlightTour;
